
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import KeySelected from "@/components/keys/KeySelected.vue";
import KeyStatus from "@/components/keys/KeyStatus.vue";
import RelayGiveKeyToGuest from "@/components/relays/RelayGiveKeyToGuest.vue";
import RelayBookedKey from "@/components/relays/RelayBookedKey.vue";
import KeyGuestReminder from "@/components/keys/guests/KeyGuestReminder.vue";
import { Getter } from "vuex-class";
import Timeout = NodeJS.Timeout;
import SimpleDialog from "@/components/SimpleDialog.vue";
import CustomChipLink from "@/components/CustomChipLink.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiEmailSyncOutline } from "@mdi/js";
import { ROWS_PER_PAGE } from "@/utils/constants";
import { getTokenLink } from "@/utils/misc";

export const DEBOUNCE_TIME_MILLI: number = 500;

@Component({
  methods: {
    mdiEmailSyncOutline() {
      return mdiEmailSyncOutline;
    },
  },
  components: {
    KeyStatus,
    RelayGiveKeyToGuest,
    KeyGuestReminder,
    RelayBookedKey,
    SimpleDialog,
    KeySelected,
    CustomChipLink,
    SvgIcon,
  },
})
export default class RelayKeysDesktop extends Vue {

  get lang(): string {
    return this.$root.$i18n.locale;
  }

  private get canArchiveAllSelectedKeys(): boolean {
    return this.selectedKeys?.every((key: any) => RelayKeysDesktop.isKeyStatusArchivable(key));
  }

  private static isKeyStatusArchivable(key: any): boolean {
    const unauthorizedStatus: string[] = ["ARCHIVED", "IN_RELAY", "IN_TRANSIT"];
    return !unauthorizedStatus.includes(key.status);
  }

  @Prop({ default: {} })
  public relay: any;

  @Getter private currentUser: any;
  @Getter private isAdmin: boolean;

  private loading: boolean = true;
  private keys: any[] = [];
  private selectedKeys: object[] = [];
  private keysStatus: string[] = [];
  private archiveAllDialog: boolean = false;
  private loadingArchived: boolean = false;
  private displayKeysFilter: boolean = false;
  private displayCategoryFilter: boolean = false;
  private timerId: Timeout = null;
  private keysSearch: string = null;
  private pagination: any = {
    sortDesc: [true],
    sortBy: ["updatedAt"],
    search: "",
    select: [],
    category: [],
    page: 1,
    itemsPerPage: 10,
  };
  private ROWS_PER_PAGE = ROWS_PER_PAGE;
  private readonly getTokenLink = getTokenLink;

  private totalKeys: number = 0;
  private headers: any[] = [
    { value: "name", sortable: true },
    { value: "status", sortable: true },
    { value: "category", sortable: false },
    { value: "location", sortable: false },
    { value: "actions", sortable: false },
  ];
  private categories: any[] = [];
  private keyGuestReminder = {
    modal: false,
    keyId: null,
  };

  @Watch("pagination", { deep: true })
  public handler(val, oldVal) {
    this.loading = true;
    if (val.page === oldVal.page) {
      val.page = 1;
    }
    this.getKeys();
  }

  private mounted() {
    this.getKeysStatus();
    this.getCategories();
  }

  private close(): void {
    this.archiveAllDialog = false;
  }

  private deselectAllKeys(): void {
    this.selectedKeys = [];
  }

  private displayArchiveAllDialog(): void {
    this.archiveAllDialog = true;
  }

  private archiveSelectedKeys() {
    this.loadingArchived = true;
    Promise.all(this.selectedKeys.map((key: any) => this.$store
      .dispatch("keys/archive", { keyId: key.id }),
    ))
      .then(async (res) => {
        this.$store.commit("alerts/displaySuccess", {
          msg: this.$i18n?.tc("actions.archived", this.selectedKeys?.length),
        });
        await this.getKeys();
        this.close();
      })
      .catch((err) => {
        this.$store.commit("alerts/displayError", {
          msg: err,
        });
      })
      .finally(() => this.loadingArchived = false);
  }

  private async openKeyGuestReminder(item) {
    this.keyGuestReminder.keyId = item.id;
    this.keyGuestReminder.modal = true;
  }

  private closeKeyGuestReminder() {
    this.keyGuestReminder = {
      modal: false,
      keyId: null,
    };
  }

  private enterKeyPress(e) {
    if (e.keyCode === 13) {
      this.searchHandler();
    }
  }

  private searchHandler(): void {
    this.loading = true;
    // cancel pending call
    clearTimeout(this.timerId);
    // delay new call
    this.timerId = setTimeout(() => {
      this.pagination.search = this.keysSearch || null;
    }, DEBOUNCE_TIME_MILLI);
  }

  private getCategories(): void {
    this.$store.dispatch("categories/getAll")
      .then((res) => {
        this.categories = res.categories;
      });
  }

  private getKeys() {
    const query = Object.assign({}, this.pagination, {
      filter: {
        relay: {
          id: this.relay.id,
          type: this.relay.type,
        },
      },
    });
    const relayId = this.relay.id;
    this.$store.dispatch("relays/getKeys", { relayId, query })
      .then((res) => {
        this.totalKeys = res?.count;
        this.keys = res?.keys;
        this.keys.forEach((key: any) => {
          // Display either current location or reserved location if the key is not in relay
          key.locationDisplayed = key?.currentLocation || key?.ReservedLocations?.[0];
          // Display either current relay or main relay of the key (agency for b2b, first else)
          key.relayDisplayed = key?.currentLocation?.Box?.Building?.Relays?.[0]
            || key.Relays.find((relay) => relay.type === "AGENCY")
            || key.Relays[0];
          this.$set(key, "newGuests", []);
        });
      }).catch((err) => {
        this.$store.commit("alerts/displayError", {
          msg: err,
        });
      }).finally(() => {
        this.loading = false;
      });
  }

  private getKeysStatus() {
    return this.$store.dispatch("keys/getStatus").then((res) => {
      this.keysStatus = res.keysStatus;
    });
  }

  private currentLocation(item) {
    return item?.KeyLocation?.find((kL) => !kL.endDate);
  }

  private isKeyInConnectedBox(item): boolean {
    return !!this.currentLocation(item)?.Location?.Box?.ConnectedBox;
  }
}
